import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllUsers } from '../api/Users';
import { User, IComplianceResult } from '../types';
import api from '../utils/api';

interface UserWithCompliance extends User {
  cycles: IComplianceResult[];
  complianceRate: number; // percentage of compliant cycles
  totalCycles: number;
  compliantCycles: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<UserWithCompliance[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    suspendedUsers: 0,
    totalCycles: 0,
    compliantCycles: 0,
    atRiskCycles: 0,
    overallComplianceRate: 0
  });

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    setLoading(true);
    setError('');
    try {
      const allUsers = await getAllUsers();

      // For each user, fetch their compliance summary
      const usersWithCompliance: UserWithCompliance[] = await Promise.all(
        allUsers.map(async (user: User) => {
          try {
            const response = await api.get(`/api/users/${user.id || user._id}/compliance-summary`);
            const cycles: IComplianceResult[] = response.data;
            
            const compliantCycles = cycles.filter((c: IComplianceResult) => c.isMet).length;
            const totalCycles = cycles.length;
            const complianceRate = totalCycles > 0 ? (compliantCycles / totalCycles) * 100 : 0;
            
            return {
              ...user,
              cycles,
              totalCycles,
              compliantCycles,
              complianceRate
            } as UserWithCompliance;
          } catch {
            // If user has no cycles or error, return with empty data
            return {
              ...user,
              cycles: [],
              totalCycles: 0,
              compliantCycles: 0,
              complianceRate: 0
            } as UserWithCompliance;
          }
        })
      );

      setUsers(usersWithCompliance);

      // Calculate statistics
      const totalUsers = usersWithCompliance.length;
      const activeUsers = usersWithCompliance.filter((u: UserWithCompliance) => !u.suspended).length;
      const suspendedUsers = usersWithCompliance.filter((u: UserWithCompliance) => u.suspended).length;
      const totalCycles = usersWithCompliance.reduce((sum: number, u: UserWithCompliance) => sum + u.totalCycles, 0);
      const compliantCycles = usersWithCompliance.reduce((sum: number, u: UserWithCompliance) => sum + u.compliantCycles, 0);
      const atRiskCycles = usersWithCompliance.reduce((sum: number, u: UserWithCompliance) => {
        return sum + u.cycles.filter((c: IComplianceResult) => !c.isMet && c.daysRemaining !== undefined && c.daysRemaining <= 30).length;
      }, 0);
      const overallComplianceRate = totalCycles > 0 ? (compliantCycles / totalCycles) * 100 : 0;

      setStats({
        totalUsers,
        activeUsers,
        suspendedUsers,
        totalCycles,
        compliantCycles,
        atRiskCycles,
        overallComplianceRate
      });

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Per-profession badge colors. User-specified: ARCH=#10B981, PE=#182745.
  const PROFESSION_COLORS: Record<string, { bg: string; color: string }> = {
    ARCH:  { bg: '#10B981', color: '#fff'     },
    PE:    { bg: '#182745', color: '#fff'     },
    CPA:   { bg: '#F59E0B', color: '#fff'     },
    LAW:   { bg: '#2D3748', color: '#fff'     },
    DENT:  { bg: '#EF4444', color: '#fff'     },
    DDS:   { bg: '#EF4444', color: '#fff'     },
    PHARM: { bg: '#FFCD00', color: '#182745'  },
    LCSW:  { bg: '#8BF559', color: '#182745'  },
    RE:    { bg: '#E2F6EF', color: '#065f46'  },
    LMHC:  { bg: '#FEF3C7', color: '#92400e' },
  };

  const getProfessionBadgeStyle = (professionID: string | undefined) => {
    if (!professionID) return { bg: '#EDE8DE', color: '#374151' };
    const key = professionID.toUpperCase().replace(/^[A-Z]{2}-/, '');
    return PROFESSION_COLORS[key] || PROFESSION_COLORS[professionID.toUpperCase()] || { bg: '#EDE8DE', color: '#374151' };
  };

  const handleDownloadUserPDF = async (userId: string, userName: string) => {
    try {
      const response = await api.get(`/api/cycles/user/${userId}/pdf`, {
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${userName.replace(/\s+/g, '_')}_compliance_report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 gap-3">
        <h2 className="mb-0">Admin Dashboard</h2>
        <div className="d-flex flex-wrap gap-2">
          <Link to="/admin/panel"           className="btn btn-sm btn-outline-secondary">User Management</Link>
          <Link to="/admin/class-browser"   className="btn btn-sm btn-outline-secondary">Class Browser</Link>
          <Link to="/admin/ai-page-designs" className="btn btn-sm btn-outline-secondary">AI Page Design</Link>
          <Link to="/admin/marketing-email" className="btn btn-sm btn-outline-secondary">Marketing Email</Link>
          <Link to="/admin/linkedin"        className="btn btn-sm btn-outline-secondary">LinkedIn</Link>
          <Link to="/admin/onboarding"      className="btn btn-sm btn-outline-secondary">Onboarding Email</Link>
          <Link to="/admin/ai-tools"        className="btn btn-sm btn-outline-secondary">AI Agents</Link>
          <Link to="/admin/ce-audit"        className="btn btn-sm btn-outline-secondary">CE Audit</Link>
          <Link to="/admin/certificates"    className="btn btn-sm btn-outline-secondary">Certificates</Link>
          <Link to="/states"               className="btn btn-sm btn-outline-secondary">State Management</Link>
          <Link to="/professions"          className="btn btn-sm btn-outline-secondary">Profession Management</Link>
          <Link to="/admin/settings"       className="btn btn-sm btn-outline-secondary">Email Settings</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Statistics Overview */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total Users',     value: stats.totalUsers,                             colorClass: 'cec-card-navy',  sub: `${stats.activeUsers} active, ${stats.suspendedUsers} suspended` },
          { label: 'Total Cycles',    value: stats.totalCycles,                            colorClass: 'cec-card-cyan',  sub: 'Across all users' },
          { label: 'Compliance Rate', value: `${stats.overallComplianceRate.toFixed(1)}%`, colorClass: 'cec-card-green', sub: `${stats.compliantCycles} of ${stats.totalCycles}` },
          { label: 'At Risk',         value: stats.atRiskCycles,                           colorClass: 'cec-card-amber', sub: 'Cycles due within 30d' },
        ].map(({ label, value, colorClass, sub }) => (
          <div key={label} className="col-6 col-md-3">
            <div className={`card cec-stat-card ${colorClass}`}>
              <div className="card-body p-3">
                <div className="cec-stat-label">{label}</div>
                <div className="cec-stat-value">{value}</div>
                <div className="cec-stat-sub">{sub}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bulk Actions */}
      <div className="card mb-4">
        <div className="card-header cec-section-header">
          <span className="cec-section-title">Bulk Operations</span>
        </div>
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row gap-2">
            <Link
              to="/admin/onboarding"
              className="btn btn-warning btn-sm"
            >
              📧 <span className="d-none d-sm-inline">At-Risk Reminders</span>
              {stats.atRiskCycles > 0 && <span className="ms-1">({stats.atRiskCycles})</span>}
            </Link>
            <button
              className="btn btn-outline-primary"
              onClick={fetchAdminData}
            >
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* User Compliance Table */}
      <div className="card mb-5">
        <div className="card-header cec-section-header">
          <span className="cec-section-title">User Compliance Overview</span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table cec-table table-hover table-sm mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="d-none d-md-table-cell">Email</th>
                  <th className="d-none d-lg-table-cell">Plan</th>
                  <th className="d-none d-lg-table-cell">Profession</th>
                  <th className="d-none d-sm-table-cell">Status</th>
                  <th>Cycles</th>
                  <th>Rate</th>
                  <th className="d-none d-sm-table-cell">At Risk</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .sort((a, b) => {
                    // Sort by at-risk cycles first, then by compliance rate
                    const aAtRisk = a.cycles.filter((c: IComplianceResult) => !c.isMet && c.daysRemaining !== undefined && c.daysRemaining <= 30).length;
                    const bAtRisk = b.cycles.filter((c: IComplianceResult) => !c.isMet && c.daysRemaining !== undefined && c.daysRemaining <= 30).length;
                    if (aAtRisk !== bAtRisk) return bAtRisk - aAtRisk;
                    return a.complianceRate - b.complianceRate;
                  })
                  .map(user => {
                    const atRiskCount = user.cycles.filter((c: IComplianceResult) => !c.isMet && c.daysRemaining !== undefined && c.daysRemaining <= 30).length;
                    return (
                      <tr
                        key={user.id || user._id}
                        onClick={() => navigate(`/admin/users/${user.id || user._id}/view`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div className="d-flex flex-column">
                            <span>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}</span>
                            <small className="text-muted d-md-none">{user.email}</small>
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell">{user.email}</td>
                        <td className="d-none d-lg-table-cell">
                          {(() => {
                            const sub = user.subscription;
                            const plan = sub?.plan || 'free';
                            const tier = sub?.tier;
                            const iv = sub?.billingInterval === 'annual' ? 'Y' : 'M';
                            if (sub?.status === 'trialing') return <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #F59E0B' }} title="On the 60-day trial — not a paying subscriber">Trial</span>;
                            if (plan === 'free') return <span className="badge" style={{ background: '#E2F6EF', color: '#065f46' }}>Free</span>;
                            if (plan === 'pro') return <span className="badge" style={{ background: '#F59E0B', color: '#fff' }}>Pro-{iv}</span>;
                            const tierLabel = tier === 'starter' ? 'Starter' : tier === 'firm' ? 'Firm' : tier === 'practice' ? 'Practice' : 'Team';
                            return <span className="badge" style={{ background: '#10B981', color: '#fff' }}>{tierLabel}-{iv}</span>;
                          })()}
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {(() => {
                            const pid = user.professionID || 'N/A';
                            const { bg, color } = getProfessionBadgeStyle(pid);
                            return (
                              <span className="badge" style={{ background: bg, color, border: bg === '#FFFFFF' || bg === '#E2F6EF' || bg === '#FEF3C7' ? '1px solid #d1d5db' : 'none' }}>
                                {pid}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="d-none d-sm-table-cell">
                          <span className={`badge ${user.suspended ? 'bg-secondary' : 'bg-success'}`}>
                            {user.suspended ? 'Suspended' : 'Active'}
                          </span>
                        </td>
                        <td>{user.totalCycles}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="progress flex-grow-1" style={{ height: '16px', minWidth: '50px' }}>
                              <div 
                                className={`progress-bar ${user.complianceRate >= 80 ? 'bg-success' : user.complianceRate >= 50 ? 'bg-warning' : 'bg-danger'}`}
                                role="progressbar"
                                style={{ width: `${user.complianceRate}%` }}
                                aria-valuenow={user.complianceRate}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <span className="d-none d-md-inline">{user.complianceRate.toFixed(0)}%</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="d-none d-sm-table-cell">
                          {atRiskCount > 0 ? (
                            <span className="badge bg-danger">{atRiskCount}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          {user.totalCycles > 0 && (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleDownloadUserPDF(
                                user.id || user._id || '',
                                `${user.firstName} ${user.lastName}`
                              )}
                              title="Download compliance report PDF"
                            >
                              ⬇ PDF
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
